import apiService from './api';
import type { ApiResponse } from '../types';

/**
 * Time in lieu by agreement (2026-10-08). Overtime is paid unless the employee
 * has asked for time in lieu, accepted the agreement, and someone with a
 * Timesheet HR seat has approved it. Then it is banked hour for hour, from the
 * day of approval until either side ends it. Server: HHNodeServer
 * `timeInLieuAgreementService`, `/api/time-in-lieu`.
 */

export type TilStatus = 'pending' | 'approved' | 'declined' | 'cancelled' | 'ended';

export interface TilAgreement {
  id: number;
  user_id: number;
  status: TilStatus;
  agreement_version: string;
  agreement_hash: string;
  agreement_text: string;
  requested_at: string;
  decided_at: string | null;
  decision_note: string | null;
  /** YYYY-MM-DD, set on approval. */
  effective_from: string | null;
  /** YYYY-MM-DD; not in force from this day. */
  ended_on: string | null;
  ended_at: string | null;
  end_reason: string | null;
  employee_name: string | null;
  employee_username: string | null;
  pay_frequency: string | null;
  decided_by_name: string | null;
  ended_by_name: string | null;
  ended_by_employee: boolean | null;
  /** On the HR list only: in force today. */
  in_force?: boolean;
}

export interface TilAgreementText {
  version: string;
  text: string;
  hash: string;
}

export interface MyTilStatus {
  /** What overtime worked today becomes. */
  choice: 'overtime' | 'time_in_lieu';
  /** The request waiting for HR, or the agreement in force. */
  open: TilAgreement | null;
  history: TilAgreement[];
  /** The text to read and accept. */
  agreement: TilAgreementText;
}

class TimeInLieuService {
  async getMine(): Promise<ApiResponse<MyTilStatus>> {
    return apiService.get('/time-in-lieu/me');
  }

  /** Ask for time in lieu, accepting exactly the text that was shown. */
  async request(agreement: TilAgreementText): Promise<ApiResponse<TilAgreement>> {
    return apiService.post('/time-in-lieu/me', { version: agreement.version, hash: agreement.hash });
  }

  async cancelRequest(): Promise<ApiResponse<null>> {
    return apiService.delete('/time-in-lieu/me');
  }

  async endMine(reason?: string): Promise<ApiResponse<TilAgreement>> {
    return apiService.post('/time-in-lieu/me/end', { reason: reason || null });
  }

  /** HR: every request and agreement, optionally for one person. */
  async list(userId?: number): Promise<ApiResponse<TilAgreement[]>> {
    return apiService.get(userId ? `/time-in-lieu?user_id=${userId}` : '/time-in-lieu');
  }

  async decide(id: number, approve: boolean, note?: string): Promise<ApiResponse<TilAgreement>> {
    return apiService.post(`/time-in-lieu/${id}/decide`, { approve, note: note || null });
  }

  async end(id: number, reason?: string): Promise<ApiResponse<TilAgreement>> {
    return apiService.post(`/time-in-lieu/${id}/end`, { reason: reason || null });
  }
}

export const timeInLieuService = new TimeInLieuService();
export default timeInLieuService;
